// After the_Kit's TopBar: page title, dark mode, alerts bell, the signed-in admin (→ My profile, S-3) and Sign out (LH-B1). The tests
// (fixtures) get the dev user switcher instead (Asha = admin, Bala = editor, Chitra = reviewer, Dev = analyst).
// Reset demo data is gone since LH-B4: the portal is always on the API.
import { Link, useLocation } from 'react-router-dom'
import { Bell, LogOut, Menu, Moon, Sun } from 'lucide-react'
import { Badge, Tooltip, applyColourScheme, initials } from '@/kit/ui'
import { useDevStore } from '@/store/devStore'
import { useSessionStore } from '@/store/sessionStore'
import { apiMode } from '@/lib/hubApi'
import { useHubStore } from '@/store/hubStore'
import { titleFor } from './menu'
import { useCurrentUser } from './useCurrentUser'

export function TopBar() {
  const location = useLocation()
  const { user } = useCurrentUser()
  const users = useHubStore((s) => s.users)
  const openAlerts = useHubStore((s) => s.alerts.filter((a) => !a.acknowledgedAt).length)
  const { colourScheme, setColourScheme, setSidebarOpen, setCurrentUser } = useDevStore()
  const signOut = useSessionStore((s) => s.signOut)

  const toggleScheme = () => {
    const next = colourScheme === 'dark' ? 'light' : 'dark'
    applyColourScheme(next)
    setColourScheme(next)
  }

  return (
    <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur dark:border-slate-700 dark:bg-surface-dark/90">
      <div className="flex h-12 items-center gap-2 px-3">
        <button type="button" className="icon-btn lg:hidden" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
          <Menu size={18} />
        </button>
        <span className="text-xs text-muted">
          Lender Hub <span aria-hidden="true">/</span> <span className="font-semibold text-default">{titleFor(location.pathname)}</span>
        </span>
        <div className="flex-1" />
        <Tooltip content={colourScheme === 'dark' ? 'Light mode' : 'Dark mode'}>
          <button type="button" className="icon-btn" onClick={toggleScheme} aria-label={colourScheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            {colourScheme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </Tooltip>
        <Link to="/alerts" className="icon-btn relative" aria-label={`Alerts, ${openAlerts} open`}>
          <Bell size={16} />
          {openAlerts > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-2xs font-bold text-white">{openAlerts}</span>
          )}
        </Link>
        <div className="flex items-center gap-2 border-l border-slate-200 pl-2 dark:border-slate-700">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-soft text-2xs font-bold text-primary-text" aria-hidden="true">
            {initials(user.name)}
          </span>
          {apiMode() ? (
            <>
              <Link to="/profile" className="hidden text-2xs hover:underline sm:inline" data-testid="signed-in-as" title="My profile: password, 2-step, sessions">{user.name}</Link>
              <Tooltip content="Sign out"><button type="button" className="icon-btn" aria-label="Sign out" onClick={() => void signOut()}><LogOut size={15} /></button></Tooltip>
            </>
          ) : (
            <select className="input !min-h-7 w-36 py-0.5 text-2xs" aria-label="Current user (dev switcher, no login yet)" value={user.id} onChange={(e) => setCurrentUser(e.target.value)}>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} · {u.role}
                </option>
              ))}
            </select>
          )}
          <Badge tone="primary" className="hidden md:inline-flex">{user.role}</Badge>
        </div>
      </div>
    </header>
  )
}
